import React, { useState } from 'react';
import { Calendar, Tag, Image as ImageIcon, FileText, Palette, CheckCircle2, Clock } from 'lucide-react';
import { getMediaUrl } from '../utils/media';
import { FormModal } from './form';

const DesignViewModal = ({ isOpen, onClose, design }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  if (!isOpen || !design) return null;

  // Helper function to get image source from various formats
  const getImageSrc = (image) => {
    if (!image) return null;

    if (typeof image === 'string') {
      if (image.startsWith('http://') || image.startsWith('https://') || image.startsWith('data:')) {
        return image;
      }
      if (image.startsWith('data:image/') || image.startsWith('/9j/') || image.startsWith('iVBORw0KGgo') || image.length > 100) {
        if (image.startsWith('/9j/')) {
          return `data:image/jpeg;base64,${image}`;
        } else if (image.startsWith('iVBORw0KGgo')) {
          return `data:image/png;base64,${image}`;
        } else if (image.startsWith('data:')) {
          return image;
        } else {
          return `data:image/jpeg;base64,${image}`;
        }
      } else {
        return getMediaUrl({ key: image });
      }
    } else if (image && typeof image === 'object') {
      if (image.url || image.downloadUrl || image.download_url) {
        return image.url || image.downloadUrl || image.download_url;
      }
      const key =
        image.s3_key ||
        image.s3Key ||
        image.key ||
        image.media_key ||
        image.mediaKey ||
        image.file_key;
      if (key) {
        return getMediaUrl({ key });
      }
      return getMediaUrl(image);
    }
    return null;
  };

  // Design themes mapping
  const designThemes = {
    1: 'Modern',
    2: 'Minimalistic',
    3: 'Traditional',
    4: 'Contemporary'
  };

  // Price range labels
  const priceRangeLabels = {
    'BUDGET': 'Budget',
    'STANDARD': 'Standard',
    'PREMIUM': 'Premium',
    'LUXURY': 'Luxury'
  };

  // Design category labels
  const designCategoryLabels = {
    'SAMPLE': 'Sample',
    'CUSTOM': 'Custom'
  };

  // Sub-tags mapping
  const subTagsMap = {
    'Sustainable': 'Sustainable',
    'Vastu-friendly': 'Vastu-friendly',
    'Space Saving': 'Space Saving',
    'kids friendly': 'Kids Friendly',
    'Budget friendly': 'Budget Friendly',
    'Pet friendly': 'Pet Friendly',
    'premium': 'Premium',
    'Smart home': 'Smart Home',
    'Elderly friendly': 'Elderly Friendly',
    'WHF setup': 'WHF Setup'
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const images = Array.isArray(design.images)
    ? design.images
    : design.images
      ? [design.images]
      : [];
  const subTags = design.subTags || design.subTagIds || [];
  const mainImage = images.length > 0 ? getImageSrc(images[selectedImageIndex]) : null;
  const isSubmitted = design.submissionStatus === 'SUBMITTED';

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      title={design.title || 'Design Details'}
      size="2xl"
    >
        <div className={`mb-3 inline-flex items-center gap-2 text-xs font-medium px-2 py-1 rounded-full ${
          isSubmitted ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-700'
        }`}>
          <div className={`w-2 h-2 rounded-full ${isSubmitted ? 'bg-green-500' : 'bg-gray-400'}`} />
          {isSubmitted ? 'Submitted' : 'Draft'}
        </div>

        {/* Content */}
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
            {/* Left Column - Images */}
            <div className="lg:col-span-2 bg-gray-50">
              {images.length > 0 ? (
                <div className="p-6 space-y-4">
                  {/* Main Image */}
                  <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-white shadow-sm border border-gray-100">
                    {mainImage ? (
                      <img
                        src={mainImage}
                        alt={design.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const placeholder = e.target.nextElementSibling;
                          if (placeholder) {
                            placeholder.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div className="hidden w-full h-full bg-gray-100 items-center justify-center">
                      <ImageIcon className="text-gray-300" size={48} />
                    </div>
                  </div>

                  {/* Thumbnail Grid */}
                  {images.length > 1 && (
                    <div className="grid grid-cols-4 gap-3">
                      {images.map((image, index) => {
                        const imageSrc = getImageSrc(image);
                        return imageSrc ? (
                          <button
                            key={index}
                            onClick={() => setSelectedImageIndex(index)}
                            className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                              selectedImageIndex === index
                                ? 'border-flamingo-500 ring-2 ring-flamingo-200'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <img
                              src={imageSrc}
                              alt={`Thumbnail ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-6 flex items-center justify-center h-full min-h-[400px]">
                  <div className="text-center">
                    <ImageIcon className="mx-auto text-gray-300 mb-3" size={48} />
                    <p className="text-sm text-gray-400">No images available</p>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Details */}
            <div className="lg:col-span-1 bg-white p-6 space-y-6">
              {/* Status Badge */}
              <div className="flex items-center gap-2">
                {isSubmitted ? (
                  <CheckCircle2 className="text-green-500" size={18} />
                ) : (
                  <Clock className="text-gray-400" size={18} />
                )}
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                  isSubmitted 
                    ? 'bg-green-50 text-green-700' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {design.submissionStatus || 'DRAFT'}
                </span>
              </div>

              {/* Price - Hero */}
              <div className="pb-4 border-b border-gray-100">
                <p className="text-xs text-gray-500 mb-1">Estimated Price</p>
                <p className="text-2xl font-bold text-flamingo-600">
                  ₹{design.estimatedPrice ? parseFloat(design.estimatedPrice).toLocaleString() : 'N/A'}
                </p>
              </div>

              {/* Description */}
              {design.description && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Description</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{design.description}</p>
                </div>
              )}

              {/* Key Details */}
              <div className="space-y-3">
                {design.designThemeId && (
                  <div className="flex items-start gap-3">
                    <Palette className="text-gray-400 mt-0.5" size={16} />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-0.5">Theme</p>
                      <p className="text-sm font-medium text-gray-900">
                        {designThemes[design.designThemeId] || 'N/A'}
                      </p>
                    </div>
                  </div>
                )}

                {design.priceRange && (
                  <div className="flex items-start gap-3">
                    <Tag className="text-gray-400 mt-0.5" size={16} />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-0.5">Price Range</p>
                      <p className="text-sm font-medium text-gray-900">
                        {priceRangeLabels[design.priceRange] || design.priceRange}
                      </p>
                    </div>
                  </div>
                )}

                {design.designCategory && (
                  <div className="flex items-start gap-3">
                    <Tag className="text-gray-400 mt-0.5" size={16} />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-0.5">Category</p>
                      <p className="text-sm font-medium text-gray-900">
                        {designCategoryLabels[design.designCategory] || design.designCategory}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Sub-tags */}
              {subTags.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Tags</p>
                  <div className="flex flex-wrap gap-1.5">
                    {subTags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2.5 py-1 bg-flamingo-50 text-flamingo-700 rounded-md text-xs font-medium"
                      >
                        {typeof tag === 'string' ? (subTagsMap[tag] || tag) : tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* BOQ Document */}
              {design.boqDocumentUrl && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Document</p>
                  <a
                    href={design.boqDocumentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-flamingo-600 hover:text-flamingo-700 font-medium transition-colors"
                  >
                    <FileText size={16} />
                    View BOQ Document
                  </a>
                </div>
              )}

              {/* Dates */}
              <div className="pt-4 border-t border-gray-100 space-y-2">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Calendar size={14} />
                  <span>Created {formatDate(design.createdDate)}</span>
                </div>
                {design.submittedDate && (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Calendar size={14} />
                    <span>Submitted {formatDate(design.submittedDate)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
    </FormModal>
  );
};

export default DesignViewModal;
